import { Component, computed, inject } from '@angular/core';
import { CustomWidget } from '../widget';
import { CommonModule } from '@angular/common';
import { AbbChartComponent } from '@components/abb-chart/abb-chart.component';
import { TPipe, TRANSLATION_PREFIX } from '@pipes/t.pipe';
import { NumberPipe } from '@pipes/number.pipe';
import { t } from '@utils/strings';

@Component({
  selector: 'abb-contracts-and-tariffs-widget',
  standalone: true,
  imports: [CommonModule, AbbChartComponent, TPipe, NumberPipe],
  template: `
    @if (shouldShowPermissionAlert()) {
      <div
        class="flex items-center justify-center h-full text-center text-abb-font/70"
      >
        {{ "You don't have permission to view this property or group" | t }}
      </div>
    } @else {
      @if (isContract()) {
      <p class="font-medium text-sm text-abb-font/70">
        {{ 'Total cost of contract divided by tariffs:' | t }}
      </p>
      <div class="h-[220px] w-full pb-3 relative overflow-hidden">
        <abb-chart
          class="absolute inset-0 w-full h-full"
          [chartType]="'doughnut'"
          [dataChart]="dataChart()"
          [grow]="true"
          [useCustomLabels]="false"
          [xAxisType]="'category'"
        ></abb-chart>
      </div>
      }

      <div class="relative z-10">
        <div
          class="grid grid-cols-[max-content_1fr] text-sm divide-y divide-abb-black/[0.06] [&>div]:py-2 overflow-auto bg-white"
        >
          <div
            class="font-medium text-abb-font/70 pr-10 border-t border-abb-black/[0.06]"
          >
            {{ 'Name' | t }}
          </div>
          <div>{{ name() }}</div>
          <div class="font-medium text-abb-font/70 pr-10">
            {{ 'Type' | t }}
          </div>
          <div>{{ type() }}</div>
          <div class="font-medium text-abb-font/70 pr-10">
            {{ 'Tag' | t }}
          </div>
          <div>{{ tag() | t}}</div>
          <div class="font-medium text-abb-font/70 pr-10">
            {{ 'Devices' | t }}
          </div>
          <div>{{ devices() }}</div>
          @if (isContract()) {
          <div class="font-medium text-abb-font/70 pr-10">
            {{ 'Total cost' | t }}
          </div>
          <div>{{ totalCost() | number }}</div>
          <div class="font-medium text-abb-font/70 pr-10">
            {{ 'Tariffs' | t }}
          </div>
          <div>
            <table class="min-w-full overflow-auto text-left">
              <thead>
                <tr class="uppercase text-abb-font/70">
                  <th>{{ 'Name' }}</th>
                  <th>{{ 'Cost' }}</th>
                  <th>{{ 'Usage' }}</th>
                </tr>
              </thead>
              <tbody>
                @for (tariff of tariffs(); track $index) {
                <tr>
                  <td>{{ tariff.name }}</td>
                  <td>{{ tariff.cost | number }}</td>
                  <td>{{ tariff.usage | number }}</td>
                </tr>
                }
              </tbody>
            </table>
          </div>
          } @else {
          <div class="font-medium text-abb-font/70 pr-10">
            {{ 'Cost' | t }}
          </div>
          <div>{{ cost() | number }}</div>
          <div class="font-medium text-abb-font/70 pr-10">
            {{ 'Usage' | t }}
          </div>
          <div>{{ usage() | number }}</div>
          }
        </div>
      </div>
    }
  `,
  styles: ``,
  providers: [
    {
      provide: TRANSLATION_PREFIX,
      useValue: 'dashboards.contracts_and_tariffs',
    },
  ],
})
export class ContractsAndTariffsWidgetComponent extends CustomWidget {
  shouldShowPermissionAlert = computed(() => {
    const data = this.data();
    if (data && (data.__typename === 'ContractData' || data.__typename === 'TariffData')) {
      return data.devices.length === 0 && this.totalCost() === -1;
    }
    return false;
  });

  isContract = computed(() => this.data()?.__typename === 'ContractData');
  dataChart = computed(() => {
    const data = this.data();
    if (data && data.__typename === 'ContractData') {
      return {
        labels: data.tariffs.map((t) => t.name),
        series: [
          {
            name: t(this.prefix, `Cost`, this.translate),
            values: data.tariffs.map((t) => t.cost),
          },
        ],
      };
    }
    return { series: [] };
  });
  prefix = inject(TRANSLATION_PREFIX);

  tariffs = computed(() => {
    const data = this.data();
    if (data && data.__typename === 'ContractData') {
      return data.tariffs;
    }
    return [];
  });

  type = computed(() => {
    const data = this.data();
    if (
      data &&
      (data.__typename === 'ContractData' || data.__typename === 'TariffData')
    ) {
      return data.type;
    }
    return '';
  });
  devices = computed(() => {
    const data = this.data();
    if (
      data &&
      (data.__typename === 'ContractData' || data.__typename === 'TariffData')
    ) {
      return data.devices.join(', ');
    }
    return '';
  });
  tag = computed(() => {
    const data = this.data();
    if (
      data &&
      (data.__typename === 'ContractData' || data.__typename === 'TariffData')
    ) {
      return data.tag;
    }
    return '';
  });
  name = computed(() => {
    const data = this.data();
    if (
      data &&
      (data.__typename === 'ContractData' || data.__typename === 'TariffData')
    ) {
      return data.name;
    }
    return '';
  });
  totalCost = computed(() => {
    const data = this.data();
    if (data && data.__typename === 'ContractData') {
      return data.totalCost;
    }
    return '';
  });
  cost = computed(() => {
    const data = this.data();
    if (data && data.__typename === 'TariffData') {
      return data.cost;
    }
    return '';
  });
  usage = computed(() => {
    const data = this.data();
    if (data && data.__typename === 'TariffData') {
      return data.usage;
    }
    return '';
  });
}
